import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import type { QueryDocumentSnapshot } from 'firebase/firestore';

import { getFirebaseFirestore } from '@/lib/firebase/firestore';
import type { Candidate } from '@/models/candidate';
import type { CandidateTopicProposalDoc, ProposalDoc } from '@/types/backend-contracts';
import { buildCandidateTopicProposalQueryKey, buildPublishedProposalsByCandidateQueryKey } from '@/lib/query-cache/query-keys';
import { resolveBrowserQueryCache, type BrowserQueryCache } from '@/lib/query-cache/query-cache';

import { toMissingProposalViewModel, toProposalViewModel, type TopicProposalItem } from '@/adapters/proposal-adapter';
import type { Proposal } from '@/models/proposal';

function createCandidateTopicProposalId(candidateId: string, topicId: string): string {
  return `${candidateId}__${topicId}`;
}

async function getCandidateTopicProposalDoc(
  candidateId: string,
  topicId: string,
  cache: BrowserQueryCache = resolveBrowserQueryCache(),
): Promise<(CandidateTopicProposalDoc & { id: string }) | null> {
  const result = await cache.read(
    buildCandidateTopicProposalQueryKey(candidateId, topicId),
    async () => {
      const snapshot = await getDoc(doc(getFirebaseFirestore(), 'candidateTopicProposals', createCandidateTopicProposalId(candidateId, topicId)));

      if (!snapshot.exists()) {
        return null;
      }

      return {
        id: snapshot.id,
        ...(snapshot.data() as CandidateTopicProposalDoc),
      };
    },
    {
      staleTimeMs: 2 * 60 * 1000,
      cacheTimeMs: 20 * 60 * 1000,
      tags: ['catalog:candidateTopicProposals'],
      invalidateTagsOnFreshFetch: ['question-answer'],
    },
  );

  return result.value;
}

async function getProposalDocById(
  proposalId: string,
  cache: BrowserQueryCache = resolveBrowserQueryCache(),
): Promise<(ProposalDoc & { id: string }) | null> {
  const result = await cache.read(
    ['proposals', proposalId],
    async () => {
      const snapshot = await getDoc(doc(getFirebaseFirestore(), 'proposals', proposalId));

      if (!snapshot.exists()) {
        return null;
      }

      return {
        id: snapshot.id,
        ...(snapshot.data() as ProposalDoc),
      };
    },
    {
      staleTimeMs: 2 * 60 * 1000,
      cacheTimeMs: 20 * 60 * 1000,
      tags: ['catalog:proposals'],
      invalidateTagsOnFreshFetch: ['question-answer'],
    },
  );

  return result.value;
}

function buildProposalExcerpt(proposal: (ProposalDoc & { id: string }) | null): string | undefined {
  if (!proposal) {
    return undefined;
  }

  const excerpt = proposal.promptSnippet?.trim() || proposal.summary.trim() || proposal.officialText.trim();

  return excerpt.length > 0 ? excerpt : undefined;
}

async function getProposalReferenceExcerpts(
  proposalIds: string[],
  cache: BrowserQueryCache = resolveBrowserQueryCache(),
): Promise<Map<string, string>> {
  if (proposalIds.length === 0) {
    return new Map();
  }

  const uniqueProposalIds = [...new Set(proposalIds)];
  const excerpts = await Promise.all(
    uniqueProposalIds.map(async (proposalId) => {
      const proposal = await getProposalDocById(proposalId, cache);
      const excerpt = buildProposalExcerpt(proposal);

      return excerpt ? ([proposalId, excerpt] as const) : null;
    }),
  );

  return new Map(excerpts.filter((entry): entry is readonly [string, string] => entry !== null));
}

export async function getPublishedProposalsByCandidate(
  candidateId: string,
  options?: { cache?: BrowserQueryCache },
): Promise<Array<CandidateTopicProposalDoc & { id: string }>> {
  const cache = resolveBrowserQueryCache(options?.cache);
  const result = await cache.read(
    buildPublishedProposalsByCandidateQueryKey(candidateId),
    async () => {
      const snapshot = await getDocs(
        query(
          collection(getFirebaseFirestore(), 'candidateTopicProposals'),
          where('candidateId', '==', candidateId),
        ),
      );

      return snapshot.docs.map((document: QueryDocumentSnapshot) => ({
        id: document.id,
        ...(document.data() as CandidateTopicProposalDoc),
      })).sort((left, right) => {
        const topicComparison = left.topicId.localeCompare(right.topicId, 'pt-BR');

        if (topicComparison !== 0) {
          return topicComparison;
        }

        return left.id.localeCompare(right.id, 'pt-BR');
      });
    },
    {
      staleTimeMs: 2 * 60 * 1000,
      cacheTimeMs: 20 * 60 * 1000,
      tags: ['catalog:candidateTopicProposals'],
      invalidateTagsOnFreshFetch: ['question-answer'],
    },
  );

  return result.value;
}

export async function getPublishedProposalsByCandidateAndTopic(
  candidateId: string,
  topicId: string,
  options?: { cache?: BrowserQueryCache },
): Promise<Array<CandidateTopicProposalDoc & { id: string }>> {
  const proposal = await getCandidateTopicProposalDoc(candidateId, topicId, options?.cache);
  return proposal ? [proposal] : [];
}

export async function getProposalSource(
  candidateId: string,
  topicId: string,
  options?: { cache?: BrowserQueryCache },
): Promise<Proposal> {
  const cache = resolveBrowserQueryCache(options?.cache);
  const proposal = await getCandidateTopicProposalDoc(candidateId, topicId, cache);

  if (!proposal) {
    return toMissingProposalViewModel(candidateId, topicId);
  }

  const viewModel = toProposalViewModel(proposal, topicId);

  if (viewModel.references.length === 0) {
    return viewModel;
  }

  const excerpts = await getProposalReferenceExcerpts(proposal.proposalIds, cache);

  return {
    ...viewModel,
    references: viewModel.references.map((reference) => ({
      ...reference,
      excerpt: excerpts.get(reference.proposalId),
    })),
  };
}

export async function getProposalsForTopic(
  topicId: string,
  candidates: Candidate[],
  options?: { cache?: BrowserQueryCache },
): Promise<TopicProposalItem[]> {
  const items: TopicProposalItem[] = [];
  const orderedCandidates = [...candidates].sort((left, right) => left.name.localeCompare(right.name, 'pt-BR'));

  for (const candidate of orderedCandidates) {
    try {
      const proposal = await getCandidateTopicProposalDoc(candidate.id, topicId, options?.cache);

      if (!proposal) {
        items.push({
          candidate,
          proposal: toMissingProposalViewModel(candidate.id, topicId),
        });
        continue;
      }

      items.push({
        candidate,
        proposal: toProposalViewModel(proposal, topicId),
      });
    } catch (error) {
      console.error('Failed to load candidateTopicProposals', { candidateId: candidate.id, topicId, error });
      items.push({
        candidate,
        proposal: toMissingProposalViewModel(candidate.id, topicId),
      });
    }
  }

  return items;
}
